import { useEffect, useMemo, useState } from 'react';
import type { Configuration, OnError, OnNudge } from './contracts';
import NudgeManager from './NudgeManager';
import { isServerSideRendering } from './utilities';
import { BrowserEventElementId, Event } from '../constants';
import type { MayNudgeShowResult, SetModalStatePayload } from '../contracts';

/**
 * Get or create the browser event element.
 */
function getEventElement(): HTMLElement | null {
  let eventEl = document.getElementById(BrowserEventElementId);

  if (!eventEl) {
    document.body.insertAdjacentHTML(
      'beforeend',
      `<div id="${BrowserEventElementId}" />`
    );
  }

  return document.getElementById(BrowserEventElementId);
}

/**
 * Manages the Nudge Manager instance and modal state.
 *
 * @param {Configuration} configuration Configuration for the Nudge Manager
 * @param {OnNudge} [onNudge] Callback for when a nudge is shown
 * @param {OnError} [onError] Callback for when an error occurs
 */
export default function useNudgeManager(
  configuration: Partial<Configuration>,
  onNudge?: OnNudge,
  onError?: OnError
): void {
  const { debug } = configuration;

  const nudgeManager = useMemo(
    () =>
      isServerSideRendering || !window.merch?._nudgeManager
        ? new NudgeManager(configuration, onNudge, onError)
        : window.merch._nudgeManager,
    [configuration, onNudge, onError]
  );
  const [openModalIds, setOpenModalIds] = useState<string[]>([]);

  useEffect(() => {
    const eventEl = getEventElement();

    if (!eventEl) return;

    // May nudge show
    eventEl.addEventListener<any>(
      Event.MayNudgeShow,
      async (e: CustomEvent<string>) => {
        if (debug) {
          console.debug('Request to show nudge arrived', e.detail);
        }

        const result = await nudgeManager.mayNudgeShow(e.detail);

        if (debug) {
          console.debug('Responding to show nudge request with result', result);
        }

        eventEl.dispatchEvent(
          new CustomEvent<MayNudgeShowResult>(Event.MayNudgeShowResult, {
            detail: { nudgeId: e.detail, result }
          })
        );
      }
    );

    // Set modal state
    eventEl.addEventListener<any>(
      Event.SetModalState,
      (e: CustomEvent<SetModalStatePayload>) => {
        const { modalId, isOpen } = e.detail;

        if (isOpen) {
          setOpenModalIds(v => [...v, modalId]);
        } else {
          setOpenModalIds(v => v.filter(id => id !== modalId));
        }
      }
    );
  }, [debug, nudgeManager]);

  // Expose Nudge Manager
  useEffect(() => {
    window.merch = {
      ...window.merch,

      /** @deprecated */
      mayNudgeShow(nudgeId: string) {
        return nudgeManager.mayNudgeShow(nudgeId);
      },

      _nudgeManager: nudgeManager
    };
  }, [nudgeManager]);

  // Modal state
  useEffect(() => {
    const isModalOpen = openModalIds.length > 0;

    nudgeManager.isModalOpen = isModalOpen;

    /** @deprecated */
    window.merch = { ...window.merch, isModalOpen };
  }, [nudgeManager, openModalIds]);
}
